import { merge } from "lodash";
import { useEffect, useRef, useState } from "react";

interface UseQueueParams<T> {
  name: string;
  canSync: boolean;
  process: (jobDetails: T) => Promise<boolean>;
  options?: {
    getNext?: (queue: T[]) => T | undefined;
  };
}

const fifo = (queue: any[]) => queue[0];

const defaultOptions = {
  getNext: fifo,
};

export function useQueue<T>({
  name,
  canSync = true,
  process,
  options,
}: UseQueueParams<T>) {
  const mergedOptions = merge(defaultOptions, options);
  const [running, setRunning] = useState(false);
  const [queue, setQueue] = useState<T[]>([]);

  const queueRef = useRef<T[]>(queue);

  function updateQueue(setter: (prevState: T[]) => T[]) {
    setQueue((prev) => {
      const updatedQueue = setter(prev);
      queueRef.current = updatedQueue;
      return updatedQueue;
    });
  }

  const enqueue = (additionalWork: T | T[]) => {
    if (Array.isArray(additionalWork)) {
      updateQueue((prev) => [...prev, ...additionalWork]);
    } else {
      updateQueue((prev) => [...prev, additionalWork]);
    }
  };

  const dequeue = (work: T | T[]) => {
    if (Array.isArray(work)) {
      updateQueue((prev) => prev.filter((job) => !work.includes(job)));
    } else {
      updateQueue((prev) => prev.filter((job) => job !== work));
    }
  };

  const processQueue = async () => {
    setRunning(true);

    if (running) {
      return;
    }

    for (const job of queueRef.current) {
      try {
        await process(job);
        dequeue(job);
      } catch (e) {
        console.error(e);
        dequeue(job);
      }
    }

    setRunning(false);
  };

  useEffect(() => {
    const hasWork = queueRef.current.length >= 1;
    const shouldProcess = canSync && running === false;
    if (!hasWork || !shouldProcess) return;
    processQueue();
  }, [queue, canSync]);

  return {
    name,
    running,
    enqueue,
    dequeue,
  };
}
